import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { 
  FiPlus, FiEdit3, FiTrash2, FiEye, FiEyeOff, FiMail, 
  FiBarChart2, FiSettings, FiCheck, FiX,
  FiSave, FiAlertCircle, FiMessageSquare, FiUpload
} from 'react-icons/fi';
import { GiCoral, GiTropicalFish } from 'react-icons/gi';
import { 
  Card, Button, Input, TextArea, Select, Badge, WaveBackground 
} from '../styles/GlobalStyles';

const AdminHeader = styled(WaveBackground)`
  background: ${props => props.theme.gradients.ocean};
  color: white;
  padding: 60px 0 40px;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const AdminTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.8rem;
  }
`;

const AdminSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
`;

const AdminSection = styled.section`
  padding: 60px 0;
  background: ${props => props.theme.colors.background};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 30px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 10px;
  }
`;

const TabNavigation = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
  background: white;
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: 0.5rem;
    overflow-x: visible;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0.75rem;
    margin-bottom: 2rem;
    gap: 0.5rem;
  }
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px 24px;
  border: none;
  border-radius: ${props => props.theme.borderRadius.medium};
  background: ${props => props.active ? 
    props.theme.gradients.button : 
    'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  justify-content: center;
  white-space: nowrap;
  min-height: 48px;

  &:hover {
    background: ${props => props.active ? 
      props.theme.gradients.button : 
      props.theme.colors.backgroundSecondary};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 14px 18px;
    font-size: 0.95rem;
    min-height: 52px;
    border-radius: ${props => props.theme.borderRadius.large};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const StatCard = styled(Card)`
  text-align: center;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.ocean};
    border-color: ${props => props.theme.colors.primary};
    
    .stat-icon {
      color: ${props => props.theme.colors.primary};
      transform: scale(1.1);
    }
    
    .stat-number {
      color: ${props => props.theme.colors.primaryDark};
    }
    
    .stat-label {
      color: ${props => props.theme.colors.primary};
    }
    
    &::after {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  &::after {
    content: '→';
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1.2rem;
    color: ${props => props.theme.colors.primary};
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s ease;
  }
  
  .stat-icon {
    font-size: 3rem;
    color: ${props => props.theme.colors.primaryLight};
    margin-bottom: 1rem;
    transition: all 0.3s ease;
  }
  
  .stat-number {
    font-size: 2.5rem;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    display: block;
    margin-bottom: 0.5rem;
    transition: all 0.3s ease;
  }
  
  .stat-label {
    color: ${props => props.theme.colors.textLight};
    font-weight: 500;
    transition: all 0.3s ease;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.5rem;
    min-height: 120px;
    
    &:active {
      transform: scale(0.98);
    }
    
    .stat-icon {
      font-size: 2.5rem;
      margin-bottom: 0.75rem;
    }
    
    .stat-number {
      font-size: 2rem;
      margin-bottom: 0.25rem;
    }
    
    .stat-label {
      font-size: 0.9rem;
    }
  }
`;

const ActionSection = styled.div`
  margin-bottom: 3rem;
`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1.5rem;
    align-items: stretch;
    
    button {
      width: 100%;
      justify-content: center;
      min-height: 48px;
      font-size: 1rem;
    }
  }
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.8rem;
  font-weight: 600;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    text-align: center;
  }
`;

const ItemsTable = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.large};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.card};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    background: transparent;
    box-shadow: none;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1.5rem;
  background: ${props => props.theme.colors.backgroundSecondary};
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  border-bottom: 1px solid rgba(0, 105, 148, 0.1);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 105, 148, 0.1);
  align-items: start;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.backgroundSecondary};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: block;
    background: white;
    border-radius: ${props => props.theme.borderRadius.large};
    box-shadow: ${props => props.theme.shadows.card};
    margin-bottom: 1rem;
    padding: 1.5rem;
    border-bottom: none;
    
    &:hover {
      background: white;
      transform: translateY(-2px);
      box-shadow: ${props => props.theme.shadows.ocean};
    }
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: flex-start;
  min-height: fit-content;
  line-height: 1.4;
  
  &.badge-cell {
    align-items: flex-start;
    justify-content: flex-start;
  }
`;

const ItemInfo = styled.div`
  .item-name {
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.25rem;
  }
  
  .item-description {
    color: ${props => props.theme.colors.textLight};
    font-size: 0.9rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: 1rem;
    
    .item-name {
      font-size: 1.1rem;
      margin-bottom: 0.5rem;
    }
    
    .item-description {
      font-size: 0.95rem;
      line-height: 1.5;
      -webkit-line-clamp: 3;
    }
  }
`;

const MobileItemDetails = styled.div`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const MobileAttribute = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 0.85rem;
  
  .attribute-label {
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
  }
  
  .attribute-value {
    color: ${props => props.theme.colors.text};
  }
`;

const ItemActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    justify-content: center;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid ${props => props.theme.colors.border};
    gap: 0.75rem;
  }
`;

const ActionButton = styled.button`
  padding: 8px;
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;

  ${props => props.variant === 'edit' && `
    background: rgba(245, 158, 11, 0.1);
    color: ${props.theme.colors.warning};
    
    &:hover {
      background: rgba(245, 158, 11, 0.2);
    }
  `}

  ${props => props.variant === 'delete' && `
    background: rgba(239, 68, 68, 0.1);
    color: ${props.theme.colors.error};
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  `}

  ${props => props.variant === 'toggle' && `
    background: rgba(16, 185, 129, 0.1);
    color: ${props.theme.colors.success};
    
    &:hover {
      background: rgba(16, 185, 129, 0.2);
    }
  `}
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-width: 48px;
    min-height: 48px;
    padding: 12px;
    border-radius: ${props => props.theme.borderRadius.medium};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0;
    align-items: flex-end;
  }
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    max-height: 95vh;
    border-radius: ${props => props.theme.borderRadius.large} ${props => props.theme.borderRadius.large} 0 0;
    padding: 1.5rem;
    margin: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(0, 105, 148, 0.1);
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
    margin: -1.5rem -1.5rem 2rem -1.5rem;
    padding: 1rem 1.5rem;
    border-bottom: 2px solid ${props => props.theme.colors.border};
  }
`;

const ModalTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  font-weight: 600;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.3rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.grayLight};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${props => props.theme.borderRadius.small};
  transition: all 0.3s ease;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${props => props.theme.colors.error};
    background: rgba(239, 68, 68, 0.1);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-width: 48px;
    min-height: 48px;
    font-size: 1.4rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 1.25rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: flex-end;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 0.75rem;
    position: sticky;
    bottom: 0;
    background: white;
    margin: 2rem -1.5rem -1.5rem -1.5rem;
    padding: 1.5rem;
    border-top: 2px solid ${props => props.theme.colors.border};
    
    button {
      min-height: 48px;
      font-size: 1rem;
      font-weight: 600;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.textLight};

  .empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .empty-text {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: ${props => props.theme.colors.error};
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: 1px solid rgba(239, 68, 68, 0.2);
`;

const MessageContent = styled.div`
  .message-header {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(0, 105, 148, 0.1);
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }
  }
  
  .message-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .message-field-label {
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    font-size: 0.9rem;
  }
  
  .message-field-value {
    color: ${props => props.theme.colors.text};
  }
  
  .message-body {
    margin-top: 1rem;
  }
  
  .message-text {
    background: ${props => props.theme.colors.backgroundSecondary};
    padding: 1.5rem;
    border-radius: ${props => props.theme.borderRadius.medium};
    border-left: 4px solid ${props => props.theme.colors.primary};
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

const FileUploadContainer = styled.div`
  border: 2px dashed ${props => props.theme.colors.primaryLight};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  background: ${props => props.theme.colors.backgroundSecondary};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(0, 105, 148, 0.05);
  }

  &.dragover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(0, 105, 148, 0.1);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }

  &:disabled {
    background: ${props => props.theme.colors.grayLight};
    cursor: not-allowed;
  }
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  text-align: center;
  
  img {
    max-width: 200px;
    max-height: 150px;
    border-radius: ${props => props.theme.borderRadius.medium};
    box-shadow: ${props => props.theme.shadows.card};
  }
  
  .preview-controls {
    margin-top: 0.5rem;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
  }
`;

const RemoveImageButton = styled.button`
  background: ${props => props.theme.colors.error};
  color: white;
  border: none;
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.small};
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;

  &:hover {
    background: #dc2626;
  }
`;

const Admin = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    corals: 0,
    fish: 0,
    messages: 0,
    unreadMessages: 0
  });
  const [corals, setCorals] = useState([]);
  const [fish, setFish] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'coral', 'fish', 'edit-coral', 'edit-fish', 'view-message'
  const [editingItem, setEditingItem] = useState(null);
  const [viewingMessage, setViewingMessage] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [coralsRes, fishRes, messagesRes] = await Promise.all([
        axios.get('/api/corals/admin/all'),
        axios.get('/api/fish/admin/all'),
        axios.get('/api/contact/admin/messages')
      ]);

      setCorals(coralsRes.data);
      setFish(fishRes.data);
      setMessages(messagesRes.data);

      setStats({
        corals: coralsRes.data.length,
        fish: fishRes.data.length,
        messages: messagesRes.data.length,
        unreadMessages: messagesRes.data.filter(msg => !msg.read_status).length
      });
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowModal(true);
    
    // Reset image upload state
    setSelectedFile(null);
    setImagePreview(null);
    setUploading(false);
    
    if (type === 'coral' || type === 'edit-coral') {
      setFormData({
        name: item?.name || '',
        description: item?.description || '',
        image_url: item?.image_url || '',
        size: item?.size || '',
        color: item?.color || '',
        difficulty_level: item?.difficulty_level || 'Mittel',
        price: item?.price || '',
        available: item?.available !== undefined ? item.available : true,
        visible: item?.visible !== undefined ? item.visible : true
      });
    } else if (type === 'fish' || type === 'edit-fish') {
      setFormData({
        name: item?.name || '',
        scientific_name: item?.scientific_name || '',
        description: item?.description || '',
        image_url: item?.image_url || '',
        size: item?.size || '',
        temperament: item?.temperament || 'Friedlich',
        diet: item?.diet || 'Allesfresser',
        tank_size_min: item?.tank_size_min || '',
        price: item?.price || '',
        available: item?.available !== undefined ? item.available : true,
        visible: item?.visible !== undefined ? item.visible : true
      });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setEditingItem(null);
    setViewingMessage(null);
    setFormData({});
    setError('');
    setSelectedFile(null);
    setImagePreview(null);
    setUploading(false);
  };

  const openMessageModal = (message) => {
    setViewingMessage(message);
    setModalType('view-message');
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Bitte wählen Sie eine gültige Bilddatei aus.');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Die Datei ist zu groß. Maximale Größe: 5MB.');
        return;
      }

      setSelectedFile(file);
      setError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', selectedFile);

      const response = await axios.post('/api/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error(error.response?.data?.message || 'Fehler beim Hochladen des Bildes');
    } finally {
      setUploading(false);
    }
  };

  const removeSelectedImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      let finalFormData = { ...formData };

      // Upload image if a new file is selected
      if (selectedFile) {
        try {
          const imageUrl = await uploadImage();
          finalFormData.image_url = imageUrl;
        } catch (uploadError) {
          setError(uploadError.message);
          setSubmitting(false);
          return;
        }
      }

      let url, method;
      
      if (modalType === 'coral') {
        url = '/api/corals';
        method = 'post';
      } else if (modalType === 'edit-coral') {
        url = `/api/corals/${editingItem.id}`;
        method = 'put';
      } else if (modalType === 'fish') {
        url = '/api/fish';
        method = 'post';
      } else if (modalType === 'edit-fish') {
        url = `/api/fish/${editingItem.id}`;
        method = 'put';
      }

      await axios[method](url, finalFormData);
      await loadData();
      closeModal();
    } catch (error) {
      setError(error.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Eintrag löschen möchten?')) {
      return;
    }

    try {
      const url = type === 'coral' ? `/api/corals/${id}` : `/api/fish/${id}`;
      await axios.delete(url);
      await loadData();
    } catch (error) {
      setError(error.response?.data?.message || 'Fehler beim Löschen');
    }
  };

  const toggleAvailability = async (type, id, currentStatus) => {
    try {
      const url = type === 'coral' ? `/api/corals/${id}` : `/api/fish/${id}`;
      const item = type === 'coral' ? 
        corals.find(c => c.id === id) : 
        fish.find(f => f.id === id);
      
      if (!item) {
        setError('Element nicht gefunden');
        return;
      }
      
      // Send the complete item data with the toggled availability status
      const updatedItem = { 
        ...item, 
        available: !currentStatus 
      };
      
      console.log('Toggling availability for:', type, id, 'from', currentStatus, 'to', !currentStatus);
      
      await axios.put(url, updatedItem);
      await loadData();
    } catch (error) {
      console.error('Error toggling availability:', error);
      setError(error.response?.data?.message || 'Fehler beim Aktualisieren');
    }
  };

  const toggleVisibility = async (type, id, currentStatus) => {
    try {
      const url = type === 'coral' ? `/api/corals/${id}` : `/api/fish/${id}`;
      const item = type === 'coral' ? 
        corals.find(c => c.id === id) : 
        fish.find(f => f.id === id);
      
      await axios.put(url, { ...item, visible: !currentStatus });
      await loadData();
    } catch (error) {
      setError(error.response?.data?.message || 'Fehler beim Aktualisieren der Sichtbarkeit');
    }
  };

  const markMessageAsRead = async (messageId) => {
    try {
      await axios.put(`/api/contact/admin/messages/${messageId}/read`);
      await loadData();
    } catch (error) {
      setError('Fehler beim Markieren der Nachricht');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Nachricht löschen möchten?')) {
      return;
    }

    try {
      await axios.delete(`/api/contact/admin/messages/${messageId}`);
      await loadData();
    } catch (error) {
      setError('Fehler beim Löschen der Nachricht');
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return <LoadingState>Daten werden geladen...</LoadingState>;
    }

    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            <StatsGrid>
              <StatCard onClick={() => setActiveTab('corals')}>
                <GiCoral className="stat-icon" />
                <span className="stat-number">{stats.corals}</span>
                <span className="stat-label">Korallen</span>
              </StatCard>
              <StatCard onClick={() => setActiveTab('fish')}>
                <GiTropicalFish className="stat-icon" />
                <span className="stat-number">{stats.fish}</span>
                <span className="stat-label">Fische</span>
              </StatCard>
              <StatCard onClick={() => setActiveTab('messages')}>
                <FiMail className="stat-icon" />
                <span className="stat-number">{stats.messages}</span>
                <span className="stat-label">Nachrichten</span>
              </StatCard>
              <StatCard onClick={() => setActiveTab('messages')}>
                <FiAlertCircle className="stat-icon" />
                <span className="stat-number">{stats.unreadMessages}</span>
                <span className="stat-label">Ungelesen</span>
              </StatCard>
            </StatsGrid>
          </>
        );

      case 'corals':
        return (
          <ActionSection>
            <ActionHeader>
              <SectionTitle>Korallen verwalten</SectionTitle>
              <Button onClick={() => openModal('coral')}>
                <FiPlus size={16} />
                Neue Koralle
              </Button>
            </ActionHeader>

            {corals.length === 0 ? (
              <EmptyState>
                <GiCoral className="empty-icon" />
                <div className="empty-text">Noch keine Korallen vorhanden</div>
                <Button onClick={() => openModal('coral')}>
                  <FiPlus size={16} />
                  Erste Koralle hinzufügen
                </Button>
              </EmptyState>
            ) : (
              <ItemsTable>
                <TableHeader>
                  <div>Name & Beschreibung</div>
                  <div>Grösse</div>
                  <div>Preis</div>
                  <div>Schwierigkeit</div>
                  <div>Verfügbarkeit</div>
                  <div>Sichtbarkeit</div>
                  <div>Aktionen</div>
                </TableHeader>
                {corals.map(coral => (
                  <TableRow key={coral.id}>
                    <ItemInfo>
                      <div className="item-name">{coral.name}</div>
                      <div className="item-description">{coral.description}</div>
                    </ItemInfo>
                    
                    <MobileItemDetails>
                      {coral.size && (
                        <MobileAttribute>
                          <span className="attribute-label">Grösse:</span>
                          <span className="attribute-value">{coral.size}</span>
                        </MobileAttribute>
                      )}
                      {coral.price && (
                        <MobileAttribute>
                          <span className="attribute-label">Preis:</span>
                          <span className="attribute-value">{coral.price}</span>
                        </MobileAttribute>
                      )}
                      <MobileAttribute>
                        <span className="attribute-label">Schwierigkeit:</span>
                        <Badge variant={
                          coral.difficulty_level === 'Anfänger' ? 'emerald' :
                          coral.difficulty_level === 'Mittel' ? 'amber' : 'error'
                        }>
                          {coral.difficulty_level}
                        </Badge>
                      </MobileAttribute>
                      <MobileAttribute>
                        <span className="attribute-label">Status:</span>
                        <Badge variant={coral.available ? 'success' : 'error'} noTransform>
                          {coral.available ? 'Verfügbar' : 'Nicht verfügbar'}
                        </Badge>
                      </MobileAttribute>
                      <MobileAttribute>
                        <span className="attribute-label">Sichtbar:</span>
                        <Badge variant={coral.visible ? 'success' : 'error'} noTransform>
                          {coral.visible ? 'Ja' : 'Nein'}
                        </Badge>
                      </MobileAttribute>
                    </MobileItemDetails>
                    
                    <TableCell>{coral.size || '-'}</TableCell>
                    <TableCell>{coral.price || '-'}</TableCell>
                    <TableCell className="badge-cell">
                      <Badge variant={
                        coral.difficulty_level === 'Anfänger' ? 'emerald' :
                        coral.difficulty_level === 'Mittel' ? 'amber' : 'error'
                      }>
                        {coral.difficulty_level}
                      </Badge>
                    </TableCell>
                    <TableCell className="badge-cell">
                      <Badge variant={coral.available ? 'success' : 'error'} noTransform>
                        {coral.available ? 'Verfügbar' : 'Nicht verfügbar'}
                      </Badge>
                    </TableCell>
                    <TableCell className="badge-cell">
                      <Badge variant={coral.visible ? 'success' : 'error'} noTransform>
                        {coral.visible ? 'Sichtbar' : 'Ausgeblendet'}
                      </Badge>
                    </TableCell>
                    <ItemActions>
                      <ActionButton 
                        variant="edit" 
                        onClick={() => openModal('edit-coral', coral)}
                        title="Bearbeiten"
                      >
                        <FiEdit3 size={16} />
                      </ActionButton>
                      <ActionButton 
                        variant="toggle" 
                        onClick={() => toggleAvailability('coral', coral.id, coral.available)}
                        title={coral.available ? 'Als nicht verfügbar markieren' : 'Als verfügbar markieren'}
                      >
                        {coral.available ? <FiCheck size={16} /> : <FiX size={16} />}
                      </ActionButton>
                      <ActionButton 
                        variant="toggle" 
                        onClick={() => toggleVisibility('coral', coral.id, coral.visible)}
                        title={coral.visible ? 'Auf Website ausblenden' : 'Auf Website anzeigen'}
                      >
                        {coral.visible ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </ActionButton>
                      <ActionButton 
                        variant="delete" 
                        onClick={() => handleDelete('coral', coral.id)}
                        title="Löschen"
                      >
                        <FiTrash2 size={16} />
                      </ActionButton>
                    </ItemActions>
                  </TableRow>
                ))}
              </ItemsTable>
            )}
          </ActionSection>
        );

      case 'fish':
        return (
          <ActionSection>
            <ActionHeader>
              <SectionTitle>Fische verwalten</SectionTitle>
              <Button onClick={() => openModal('fish')}>
                <FiPlus size={16} />
                Neuer Fisch
              </Button>
            </ActionHeader>

            {fish.length === 0 ? (
              <EmptyState>
                <GiTropicalFish className="empty-icon" />
                <div className="empty-text">Noch keine Fische vorhanden</div>
                <Button onClick={() => openModal('fish')}>
                  <FiPlus size={16} />
                  Ersten Fisch hinzufügen
                </Button>
              </EmptyState>
            ) : (
              <ItemsTable>
                <TableHeader>
                  <div>Name & Beschreibung</div>
                  <div>Grösse</div>
                  <div>Preis</div>
                  <div>Temperament</div>
                  <div>Verfügbarkeit</div>
                  <div>Sichtbarkeit</div>
                  <div>Aktionen</div>
                </TableHeader>
                {fish.map(fishItem => (
                  <TableRow key={fishItem.id}>
                    <ItemInfo>
                      <div className="item-name">{fishItem.name}</div>
                      <div className="item-description">{fishItem.description}</div>
                    </ItemInfo>
                    
                    <MobileItemDetails>
                      {fishItem.size && (
                        <MobileAttribute>
                          <span className="attribute-label">Grösse:</span>
                          <span className="attribute-value">{fishItem.size}</span>
                        </MobileAttribute>
                      )}
                      {fishItem.price && (
                        <MobileAttribute>
                          <span className="attribute-label">Preis:</span>
                          <span className="attribute-value">{fishItem.price}</span>
                        </MobileAttribute>
                      )}
                      {fishItem.scientific_name && (
                        <MobileAttribute>
                          <span className="attribute-label">Wiss. Name:</span>
                          <span className="attribute-value">{fishItem.scientific_name}</span>
                        </MobileAttribute>
                      )}
                      <MobileAttribute>
                        <span className="attribute-label">Temperament:</span>
                        <Badge variant={
                          fishItem.temperament === 'Friedlich' ? 'emerald' :
                          fishItem.temperament === 'Semi-Aggressiv' ? 'amber' : 'error'
                        }>
                          {fishItem.temperament}
                        </Badge>
                      </MobileAttribute>
                      <MobileAttribute>
                        <span className="attribute-label">Status:</span>
                        <Badge variant={fishItem.available ? 'success' : 'error'} noTransform>
                          {fishItem.available ? 'Verfügbar' : 'Nicht verfügbar'}
                        </Badge>
                      </MobileAttribute>
                      <MobileAttribute>
                        <span className="attribute-label">Sichtbar:</span>
                        <Badge variant={fishItem.visible ? 'success' : 'error'} noTransform>
                          {fishItem.visible ? 'Ja' : 'Nein'}
                        </Badge>
                      </MobileAttribute>
                    </MobileItemDetails>
                    
                    <TableCell>{fishItem.size || '-'}</TableCell>
                    <TableCell>{fishItem.price || '-'}</TableCell>
                    <TableCell className="badge-cell">
                      <Badge variant={
                        fishItem.temperament === 'Friedlich' ? 'emerald' :
                        fishItem.temperament === 'Semi-Aggressiv' ? 'amber' : 'error'
                      }>
                        {fishItem.temperament}
                      </Badge>
                    </TableCell>
                    <TableCell className="badge-cell">
                      <Badge variant={fishItem.available ? 'success' : 'error'} noTransform>
                        {fishItem.available ? 'Verfügbar' : 'Nicht verfügbar'}
                      </Badge>
                    </TableCell>
                    <TableCell className="badge-cell">
                      <Badge variant={fishItem.visible ? 'success' : 'error'} noTransform>
                        {fishItem.visible ? 'Sichtbar' : 'Ausgeblendet'}
                      </Badge>
                    </TableCell>
                    <ItemActions>
                      <ActionButton 
                        variant="edit" 
                        onClick={() => openModal('edit-fish', fishItem)}
                        title="Bearbeiten"
                      >
                        <FiEdit3 size={16} />
                      </ActionButton>
                      <ActionButton 
                        variant="toggle" 
                        onClick={() => toggleAvailability('fish', fishItem.id, fishItem.available)}
                        title={fishItem.available ? 'Als nicht verfügbar markieren' : 'Als verfügbar markieren'}
                      >
                        {fishItem.available ? <FiCheck size={16} /> : <FiX size={16} />}
                      </ActionButton>
                      <ActionButton 
                        variant="toggle" 
                        onClick={() => toggleVisibility('fish', fishItem.id, fishItem.visible)}
                        title={fishItem.visible ? 'Auf Website ausblenden' : 'Auf Website anzeigen'}
                      >
                        {fishItem.visible ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </ActionButton>
                      <ActionButton 
                        variant="delete" 
                        onClick={() => handleDelete('fish', fishItem.id)}
                        title="Löschen"
                      >
                        <FiTrash2 size={16} />
                      </ActionButton>
                    </ItemActions>
                  </TableRow>
                ))}
              </ItemsTable>
            )}
          </ActionSection>
        );

      case 'messages':
        return (
          <ActionSection>
            <ActionHeader>
              <SectionTitle>Kontaktanfragen</SectionTitle>
            </ActionHeader>

            {messages.length === 0 ? (
              <EmptyState>
                <FiMail className="empty-icon" />
                <div className="empty-text">Noch keine Nachrichten erhalten</div>
              </EmptyState>
            ) : (
              <ItemsTable>
                <TableHeader>
                  <div>Nachricht</div>
                  <div>Datum</div>
                  <div>Kategorie</div>
                  <div>Status</div>
                  <div>Aktionen</div>
                </TableHeader>
                {messages.map(message => (
                  <TableRow key={message.id}>
                    <ItemInfo>
                      <div className="item-name">{message.name} ({message.email})</div>
                      <div className="item-description">
                        <strong>{message.subject}</strong><br />
                        {message.message.length > 100 ? 
                          message.message.substring(0, 100) + '...' : 
                          message.message
                        }
                      </div>
                    </ItemInfo>
                    
                    <MobileItemDetails>
                      <MobileAttribute>
                        <span className="attribute-label">Datum:</span>
                        <span className="attribute-value">
                          {new Date(message.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </MobileAttribute>
                      <MobileAttribute>
                        <span className="attribute-label">Kategorie:</span>
                        <Badge variant="primary">{message.item_type}</Badge>
                      </MobileAttribute>
                      <MobileAttribute>
                        <span className="attribute-label">Status:</span>
                        <Badge variant={message.read_status ? 'success' : 'warning'}>
                          {message.read_status ? 'Gelesen' : 'Ungelesen'}
                        </Badge>
                      </MobileAttribute>
                    </MobileItemDetails>
                    
                    <div>
                      {new Date(message.created_at).toLocaleDateString('de-DE')}
                    </div>
                    <div>
                      <Badge variant="primary">{message.item_type}</Badge>
                    </div>
                    <div>
                      <Badge variant={message.read_status ? 'success' : 'warning'}>
                        {message.read_status ? 'Gelesen' : 'Ungelesen'}
                      </Badge>
                    </div>
                    <ItemActions>
                      <ActionButton 
                        variant="edit" 
                        onClick={() => openMessageModal(message)}
                        title="Nachricht anzeigen"
                      >
                        <FiMessageSquare size={16} />
                      </ActionButton>
                      {!message.read_status && (
                        <ActionButton 
                          variant="toggle" 
                          onClick={() => markMessageAsRead(message.id)}
                          title="Als gelesen markieren"
                        >
                          <FiCheck size={16} />
                        </ActionButton>
                      )}
                      <ActionButton 
                        variant="delete" 
                        onClick={() => deleteMessage(message.id)}
                        title="Löschen"
                      >
                        <FiTrash2 size={16} />
                      </ActionButton>
                    </ItemActions>
                  </TableRow>
                ))}
              </ItemsTable>
            )}
          </ActionSection>
        );

      default:
        return null;
    }
  };

  const renderModal = () => {
    if (!showModal) return null;

    if (modalType === 'view-message') {
      return (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Nachricht Details</ModalTitle>
              <CloseButton onClick={closeModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <MessageContent>
              <div className="message-header">
                <div className="message-field">
                  <div className="message-field-label">Name</div>
                  <div className="message-field-value">{viewingMessage?.name}</div>
                </div>
                <div className="message-field">
                  <div className="message-field-label">E-Mail</div>
                  <div className="message-field-value">{viewingMessage?.email}</div>
                </div>
                <div className="message-field">
                  <div className="message-field-label">Betreff</div>
                  <div className="message-field-value">{viewingMessage?.subject}</div>
                </div>
                <div className="message-field">
                  <div className="message-field-label">Kategorie</div>
                  <div className="message-field-value">
                    <Badge variant="primary">{viewingMessage?.item_type}</Badge>
                  </div>
                </div>
                <div className="message-field">
                  <div className="message-field-label">Datum</div>
                  <div className="message-field-value">
                    {viewingMessage?.created_at ? 
                      new Date(viewingMessage.created_at).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '-'}
                  </div>
                </div>
                <div className="message-field">
                  <div className="message-field-label">Status</div>
                  <div className="message-field-value">
                    <Badge variant={viewingMessage?.read_status ? 'success' : 'warning'}>
                      {viewingMessage?.read_status ? 'Gelesen' : 'Ungelesen'}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="message-body">
                <div className="message-field-label">Nachricht</div>
                <div className="message-text">{viewingMessage?.message}</div>
              </div>
            </MessageContent>

            <ModalActions>
              {viewingMessage && !viewingMessage.read_status && (
                <Button 
                  onClick={() => {
                    markMessageAsRead(viewingMessage.id);
                    closeModal();
                  }}
                >
                  <FiCheck size={16} />
                  Als gelesen markieren
                </Button>
              )}
              <Button type="button" outline onClick={closeModal}>
                Schließen
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      );
    }

    const isCoralType = modalType.includes('coral');
    const isEditing = modalType.includes('edit');

    return (
      <Modal>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {isEditing ? 'Bearbeiten' : 'Hinzufügen'}: {isCoralType ? 'Koralle' : 'Fisch'}
            </ModalTitle>
            <CloseButton onClick={closeModal}>
              <FiX />
            </CloseButton>
          </ModalHeader>

          {error && (
            <ErrorMessage>
              <FiAlertCircle size={20} />
              {error}
            </ErrorMessage>
          )}

          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormGroup>
                <Label>Name *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  placeholder={`Name ${isCoralType ? 'der Koralle' : 'des Fisches'}`}
                  required
                />
              </FormGroup>

              {!isCoralType && (
                <FormGroup>
                  <Label>Wissenschaftlicher Name</Label>
                  <Input
                    type="text"
                    name="scientific_name"
                    value={formData.scientific_name || ''}
                    onChange={handleInputChange}
                    placeholder="z.B. Amphiprion ocellaris"
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label>Beschreibung *</Label>
                <TextArea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  placeholder={`Beschreibung ${isCoralType ? 'der Koralle' : 'des Fisches'}`}
                  required
                  rows={4}
                />
              </FormGroup>

              <FormGroup>
                <Label>Bild {selectedFile || formData.image_url ? '' : '*'}</Label>
                <FileUploadContainer>
                  <FileInput
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                  />
                  <FileUploadButton
                    type="button"
                    onClick={() => document.getElementById('image-upload').click()}
                    disabled={uploading}
                  >
                    <FiUpload size={16} />
                    {uploading ? 'Wird hochgeladen...' : 'Bild auswählen'}
                  </FileUploadButton>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>
                    JPG, PNG, WebP • Max. 5MB
                  </div>
                  
                  {/* Show current image URL if editing and no new file selected */}
                  {!selectedFile && formData.image_url && (
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        Aktuelles Bild:
                      </div>
                      <img 
                        src={formData.image_url} 
                        alt="Current" 
                        style={{ 
                          maxWidth: '200px', 
                          maxHeight: '150px', 
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }} 
                      />
                    </div>
                  )}
                </FileUploadContainer>
                
                {/* Image Preview for selected file */}
                {imagePreview && (
                  <ImagePreview>
                    <img src={imagePreview} alt="Preview" />
                    <div className="preview-controls">
                      <RemoveImageButton type="button" onClick={removeSelectedImage}>
                        <FiX size={14} />
                        Entfernen
                      </RemoveImageButton>
                    </div>
                  </ImagePreview>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Grösse</Label>
                <Input
                  type="text"
                  name="size"
                  value={formData.size || ''}
                  onChange={handleInputChange}
                  placeholder={isCoralType ? "z.B. 10-15cm" : "z.B. 8-10cm"}
                />
              </FormGroup>

              {isCoralType ? (
                <>
                  <FormGroup>
                    <Label>Farbe</Label>
                    <Input
                      type="text"
                      name="color"
                      value={formData.color || ''}
                      onChange={handleInputChange}
                      placeholder="z.B. Grün/Gold"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Schwierigkeitsgrad</Label>
                    <Select
                      name="difficulty_level"
                      value={formData.difficulty_level || 'Mittel'}
                      onChange={handleInputChange}
                    >
                      <option value="Anfänger">Anfänger</option>
                      <option value="Mittel">Mittel</option>
                      <option value="Fortgeschritten">Fortgeschritten</option>
                    </Select>
                  </FormGroup>
                </>
              ) : (
                <>
                  <FormGroup>
                    <Label>Temperament</Label>
                    <Select
                      name="temperament"
                      value={formData.temperament || 'Friedlich'}
                      onChange={handleInputChange}
                    >
                      <option value="Friedlich">Friedlich</option>
                      <option value="Semi-Aggressiv">Semi-Aggressiv</option>
                      <option value="Aggressiv">Aggressiv</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Ernährung</Label>
                    <Select
                      name="diet"
                      value={formData.diet || 'Allesfresser'}
                      onChange={handleInputChange}
                    >
                      <option value="Pflanzenfresser">Pflanzenfresser</option>
                      <option value="Fleischfresser">Fleischfresser</option>
                      <option value="Allesfresser">Allesfresser</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Mindest-Beckengrösse (Liter)</Label>
                    <Input
                      type="number"
                      name="tank_size_min"
                      value={formData.tank_size_min || ''}
                      onChange={handleInputChange}
                      placeholder="z.B. 200"
                      min="0"
                    />
                  </FormGroup>
                </>
              )}

              <FormGroup>
                <Label>Preis</Label>
                <Input
                  type="text"
                  name="price"
                  value={formData.price || ''}
                  onChange={handleInputChange}
                  placeholder="z.B. 25-35 oder 20-30 (CHF wird automatisch hinzugefügt)"
                />
              </FormGroup>

              {isEditing && (
                <FormGroup>
                  <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="available"
                      checked={formData.available || false}
                      onChange={handleInputChange}
                    />
                    Verfügbar
                  </Label>
                </FormGroup>
              )}
            </FormGrid>

            <ModalActions>
              <Button type="button" outline onClick={closeModal}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={submitting || uploading}>
                <FiSave size={16} />
                {uploading ? 'Bild wird hochgeladen...' : submitting ? 'Wird gespeichert...' : 'Speichern'}
              </Button>
            </ModalActions>
          </form>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <>
      <AdminHeader>
        <HeaderContent>
          <AdminTitle>
            <FiSettings size={50} />
            Admin Dashboard
          </AdminTitle>
          <AdminSubtitle>
            Verwalten Sie Ihre Korallen, Fische und Kontaktanfragen
          </AdminSubtitle>
        </HeaderContent>
      </AdminHeader>

      <AdminSection>
        <Container>
          {error && (
            <ErrorMessage>
              <FiAlertCircle size={20} />
              {error}
            </ErrorMessage>
          )}

          <TabNavigation>
            <TabButton 
              active={activeTab === 'dashboard'} 
              onClick={() => setActiveTab('dashboard')}
            >
              <FiBarChart2 size={18} />
              Dashboard
            </TabButton>
            <TabButton 
              active={activeTab === 'corals'} 
              onClick={() => setActiveTab('corals')}
            >
              <GiCoral size={18} />
              Korallen ({stats.corals})
            </TabButton>
            <TabButton 
              active={activeTab === 'fish'} 
              onClick={() => setActiveTab('fish')}
            >
              <GiTropicalFish size={18} />
              Fische ({stats.fish})
            </TabButton>
            <TabButton 
              active={activeTab === 'messages'} 
              onClick={() => setActiveTab('messages')}
            >
              <FiMail size={18} />
              Nachrichten ({stats.unreadMessages})
            </TabButton>
          </TabNavigation>

          {renderTabContent()}
        </Container>
      </AdminSection>

      {renderModal()}
    </>
  );
};

export default Admin;

